import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Chart } from 'primereact/chart';
import { HiltiMaterial, HiltiProduct } from '../types';
import { hiltiMaterialsAPI, hiltiProductsAPI } from '../services/api';
import { showCustomAlert } from './CustomAlert';

type StatusKey = 'stable' | 'watch' | 'low' | 'negative';

const statusConfig: Record<
  StatusKey,
  { label: string; severity: 'success' | 'info' | 'warning' | 'danger'; color: string }
> = {
  stable: { label: 'Stabilno', severity: 'success', color: '#22c55e' },
  watch: { label: 'Praćenje', severity: 'info', color: '#eab308' },
  low: { label: 'Nisko stanje', severity: 'warning', color: '#f97316' },
  negative: { label: 'U minusu', severity: 'danger', color: '#ef4444' }
};

const getStatus = (m: HiltiMaterial): StatusKey => {
  if (m.currentStock <= 0) return 'negative';
  if (!m.minStock || m.minStock <= 0) return 'stable';
  const ratio = m.currentStock / m.minStock;
  if (ratio < 0.5) return 'low';
  if (ratio < 1) return 'watch';
  return 'stable';
};

interface ProductChart {
  productId: string;
  productName: string;
  materials: HiltiMaterial[];
}

const HiltiMaterials: React.FC = () => {
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [materials, setMaterials] = useState<HiltiMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiMaterial | null>(null);
  const [form, setForm] = useState<{
    name: string;
    unit: string;
    currentStock: number;
    initialStock: number;
    minStock: number;
    description: string;
  }>({
    name: '',
    unit: 'kom',
    currentStock: 0,
    initialStock: 0,
    minStock: 0,
    description: ''
  });

  const [charts, setCharts] = useState<ProductChart[]>([]);

  const buildDateParams = () => {
    const params: { startDate?: string; endDate?: string } = {};
    if (startDate) params.startDate = startDate.toISOString().slice(0, 10);
    if (endDate) params.endDate = endDate.toISOString().slice(0, 10);
    return params;
  };

  const loadProducts = async () => {
    try {
      const data = await hiltiProductsAPI.getAll();
      setProducts(data);
      if (!selectedProductId && data.length > 0) {
        setSelectedProductId(data[0]._id);
      }
      await loadChartsForProducts(data, selectedProductId || (data[0]?._id ?? ''));
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      showCustomAlert.error(msg);
    }
  };

  const loadMaterials = async (opts?: { clearError?: boolean }) => {
    if (!selectedProductId) return;
    setLoading(true);
    if (opts?.clearError) setError('');
    try {
      const params = { productId: selectedProductId, ...buildDateParams() };
      const data = await hiltiMaterialsAPI.getAllForProduct(params);
      setMaterials(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju materijala';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      void loadMaterials({ clearError: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId]);

  const productOptions = useMemo(
    () => products.map((p) => ({ label: p.name, value: p._id })),
    [products]
  );

  const kpi = useMemo(() => {
    const activeCount = materials.length;
    const totalStock = materials.reduce((sum, m) => sum + (m.currentStock || 0), 0);
    const lowStates = materials.filter((m) => {
      const s = getStatus(m);
      return s === 'low' || s === 'negative';
    }).length;
    const totalPeriodReceived = materials.reduce(
      (sum, m) => sum + (m.periodReceived ?? 0),
      0
    );
    return { activeCount, totalStock, lowStates, totalPeriodReceived };
  }, [materials]);

  const onResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    void loadMaterials({ clearError: true });
  };

  const statusBodyTemplate = (m: HiltiMaterial) => {
    const key = getStatus(m);
    const cfg = statusConfig[key];
    return <Tag value={cfg.label} severity={cfg.severity} />;
  };

  const percentUsedTemplate = (m: HiltiMaterial) => {
    const total = m.periodReceived ?? m.totalReceived ?? 0;
    if (total <= 0) return '0%';
    const consumed = m.periodConsumed ?? 0;
    const pct = Math.max(0, Math.min(100, (consumed / total) * 100));
    return `${pct.toFixed(1)}%`;
  };

  const numberTemplate = (value: number) => (Number.isFinite(value) ? value.toLocaleString('sr-RS') : '0');

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('sr-RS');
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      unit: 'kom',
      currentStock: 0,
      initialStock: 0,
      minStock: 0,
      description: ''
    });
    setDialogVisible(true);
  };

  const openEdit = (m: HiltiMaterial) => {
    setEditing(m);
    setForm({
      name: m.name,
      unit: m.unit || 'kom',
      currentStock: m.currentStock || 0,
      initialStock: 0,
      minStock: m.minStock || 0,
      description: m.description || ''
    });
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!selectedProductId) {
      setError('Morate izabrati proizvod.');
      return;
    }
    if (!form.name.trim()) {
      setError('Naziv materijala je obavezan.');
      return;
    }

    try {
      setError('');
      if (editing) {
        await hiltiMaterialsAPI.update(editing._id, {
          name: form.name.trim(),
          unit: form.unit.trim(),
          minStock: form.minStock,
          description: form.description.trim() || '',
          currentStock: form.currentStock
        });
        showCustomAlert.success('Materijal je ažuriran');
      } else {
        await hiltiMaterialsAPI.create({
          productId: selectedProductId,
          name: form.name.trim(),
          unit: form.unit.trim(),
          initialStock: form.initialStock,
          minStock: form.minStock,
          description: form.description.trim() || ''
        });
        showCustomAlert.success('Materijal je dodat');
      }
      setDialogVisible(false);
      await loadMaterials({ clearError: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju materijala';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = async (m: HiltiMaterial) => {
    if (!window.confirm(`Da li ste sigurni da želite da obrišete materijal "${m.name}"?`)) return;
    try {
      await hiltiMaterialsAPI.delete(m._id);
      await loadMaterials({ clearError: true });
      await loadChartsForProducts(products, selectedProductId);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju materijala';
      showCustomAlert.error(msg);
    }
  };

  const loadChartsForProducts = async (productList: HiltiProduct[], currentProductId: string) => {
    if (!productList.length) {
      setCharts([]);
      return;
    }
    setLoading(true);
    try {
      const dateParams = buildDateParams();
      const responses = await Promise.all(
        productList.map((p) =>
          hiltiMaterialsAPI.getAllForProduct({ productId: p._id, ...dateParams })
        )
      );
      const chartData: ProductChart[] = productList.map((p, idx) => ({
        productId: p._id,
        productName: p.name,
        materials: responses[idx] || []
      }));
      setCharts(chartData);

      const selectedChart =
        chartData.find((c) => c.productId === currentProductId) || chartData[0];
      if (selectedChart) {
        setSelectedProductId(selectedChart.productId);
        setMaterials(selectedChart.materials);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri učitavanju grafikona materijala';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#64748b',
            maxRotation: 60,
            minRotation: 0
          }
        },
        y: {
          ticks: {
            color: '#64748b'
          },
          beginAtZero: true
        }
      }
    }),
    []
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <h2 style={{ marginBottom: '1rem' }}>Hilti magacin materijala</h2>

      <div className="grid" style={{ marginBottom: '1.5rem' }}>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-sm text-gray-500 mb-1">Materijali</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{kpi.activeCount}</div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-sm text-gray-500 mb-1">Trenutno stanje</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
              {kpi.totalStock.toLocaleString('sr-RS')}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-sm text-gray-500 mb-1">Niska stanja</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{kpi.lowStates}</div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-sm text-gray-500 mb-1">Ukupno zaprimljeno</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
              {kpi.totalPeriodReceived.toLocaleString('sr-RS')}
            </div>
          </Card>
        </div>
      </div>

      <Card title="Vremenski period">
        <div className="grid align-items-end">
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Proizvod
            </label>
            <Dropdown
              value={selectedProductId}
              options={productOptions}
              onChange={(e) => setSelectedProductId(e.value)}
              placeholder="Izaberite proizvod"
              appendTo="self"
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Vremenski period
            </label>
            <div className="flex gap-2">
              <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value as Date | null)}
                dateFormat="dd.mm.yy"
                showIcon
                appendTo="self"
                className="w-full"
              />
              <span style={{ alignSelf: 'center' }}>–</span>
              <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value as Date | null)}
                dateFormat="dd.mm.yy"
                showIcon
                appendTo="self"
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12 md:col-4 flex gap-2 justify-content-end">
            <Button
              label="Primeni"
              icon="pi pi-check"
              onClick={() => {
                void loadMaterials({ clearError: true });
                void loadChartsForProducts(products, selectedProductId);
              }}
            />
            <Button
              label="Poslednjih 7 dana"
              icon="pi pi-history"
              outlined
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 6);
                setStartDate(start);
                setEndDate(end);
                void loadMaterials({ clearError: true });
                void loadChartsForProducts(products, selectedProductId);
              }}
            />
          </div>
        </div>
        <div className="flex justify-content-end text-sm text-600 mt-2">
          <span>
            Prikaz za period:{' '}
            {startDate
              ? startDate.toLocaleDateString('sr-RS')
              : '—'}{' '}
            -{' '}
            {endDate
              ? endDate.toLocaleDateString('sr-RS')
              : '—'}
          </span>
        </div>
      </Card>

      <Card title="Pregled stanja" style={{ marginTop: '1.5rem' }}>
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={materials}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage="Nema materijala"
          responsiveLayout="stack" breakpoint="768px"
        >
          <Column field="name" header="Materijal" sortable />
          <Column
            header="Trenutno stanje"
            body={(m: HiltiMaterial) =>
              `${numberTemplate(m.currentStock || 0)} ${m.unit || 'kom'}`
            }
          />
          <Column
            header="Zaprimljeno"
            body={(m: HiltiMaterial) =>
              `${numberTemplate(m.periodReceived ?? 0)} ${m.unit || 'kom'}`
            }
          />
          <Column
            header="Potrošeno"
            body={(m: HiltiMaterial) =>
              `${numberTemplate(m.periodConsumed ?? 0)} ${m.unit || 'kom'}`
            }
          />
          <Column
            header="Rashodovano"
            body={(m: HiltiMaterial) =>
              `${numberTemplate(m.periodDisposed ?? 0)} ${m.unit || 'kom'}`
            }
          />
          <Column
            header="Iskorišćenost"
            body={percentUsedTemplate}
            style={{ width: '140px' }}
          />
          <Column
            header="Poslednje zaprimanje"
            body={(m: HiltiMaterial) => formatDate(m.lastReceiptDate as string | null)}
          />
          <Column
            header="Poslednji rashod"
            body={(m: HiltiMaterial) => formatDate(m.lastDisposalDate as string | null)}
          />
        </DataTable>
      </Card>

      {charts.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Grafik stanja materijala po proizvodima</h3>
          <div className="grid">
            {charts.map((chart) => {
              const labels = chart.materials.map((m) => m.name);
              const data = chart.materials.map((m) => m.currentStock || 0);
              const bgColors = chart.materials.map(
                (m) => statusConfig[getStatus(m)].color
              );
              const chartData = {
                labels,
                datasets: [
                  {
                    label: 'Trenutno stanje',
                    data,
                    backgroundColor: bgColors
                  }
                ]
              };

              return (
                <div key={chart.productId} className="col-12 md:col-6 lg:col-3">
                  <Card
                    title={chart.productName}
                    subTitle={`Materijali: ${chart.materials.length}`}
                    style={{ height: '100%' }}
                  >
                    <Chart type="bar" data={chartData} options={chartOptions} />
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog
        header={editing ? 'Izmeni Hilti materijal' : 'Novi Hilti materijal'}
        visible={dialogVisible}
        style={{ width: '600px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid">
          <div className="field">
            <label style={{ fontWeight: 600 }}>Naziv materijala*</label>
            <InputText
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="field">
            <label style={{ fontWeight: 600 }}>Jedinica</label>
            <InputText
              value={form.unit}
              onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
            />
          </div>

          {!editing && (
            <div className="field">
              <label style={{ fontWeight: 600 }}>Početno stanje</label>
              <InputNumber
                value={form.initialStock}
                onValueChange={(e) =>
                  setForm((prev) => ({ ...prev, initialStock: e.value || 0 }))
                }
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={3}
                className="w-full"
              />
            </div>
          )}

          {editing && (
            <div className="field">
              <label style={{ fontWeight: 600 }}>Trenutno stanje</label>
              <InputNumber
                value={form.currentStock}
                onValueChange={(e) =>
                  setForm((prev) => ({ ...prev, currentStock: e.value || 0 }))
                }
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={3}
                className="w-full"
              />
            </div>
          )}

          <div className="field">
            <label style={{ fontWeight: 600 }}>Minimalno stanje</label>
            <InputNumber
              value={form.minStock}
              onValueChange={(e) =>
                setForm((prev) => ({ ...prev, minStock: e.value || 0 }))
              }
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={3}
              className="w-full"
            />
          </div>

          <div className="field">
            <label style={{ fontWeight: 600 }}>Opis / napomena</label>
            <InputTextarea
              autoResize
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>

          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSave} />
            <Button
              label="Otkaži"
              icon="pi pi-times"
              severity="secondary"
              outlined
              onClick={() => setDialogVisible(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default HiltiMaterials;


